import { Flex, Text, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { TChainIndicator } from './types';
import type { ChainIndicatorId } from 'types/homepage';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { HOMEPAGE_STATS, HOMEPAGE_STATS_MICROSERVICE } from 'stubs/stats';
import Skeleton from 'ui/shared/chakra/Skeleton';
import Hint from 'ui/shared/Hint';
import IconSvg from 'ui/shared/IconSvg';

import ChainIndicatorChartContainer from './ChainIndicatorChartContainer';
import ChainIndicatorItem from './ChainIndicatorItem';
import useChartDataQuery from './useChartDataQuery';
import getIndicatorValues from './utils/getIndicatorValues';
import INDICATORS from './utils/indicators';

const isStatsFeatureEnabled = config.features.stats.isEnabled;

const indicators = INDICATORS.filter(({ id }) =>
  config.UI.homepage.charts.includes(id),
).sort((a, b) => {
  if (
    config.UI.homepage.charts.indexOf(a.id) >
    config.UI.homepage.charts.indexOf(b.id)
  ) {
    return 1;
  }

  if (
    config.UI.homepage.charts.indexOf(a.id) <
    config.UI.homepage.charts.indexOf(b.id)
  ) {
    return -1;
  }

  return 0;
});

const ChainIndicators = () => {
  const [ selectedIndicator, selectIndicator ] = React.useState(
    indicators[0]?.id,
  );
  const selectedIndicatorData = indicators.find(
    ({ id }) => id === selectedIndicator,
  );

  const queryResult = useChartDataQuery(
    selectedIndicatorData?.id as ChainIndicatorId,
  );

  const statsMicroserviceQueryResult = useApiQuery('stats_main', {
    queryOptions: {
      refetchOnMount: false,
      enabled: isStatsFeatureEnabled,
      placeholderData: HOMEPAGE_STATS_MICROSERVICE,
    },
  });

  const statsApiQueryResult = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });

  const bgColor = useColorModeValue('gray.50', 'whiteAlpha.100');

  if (indicators.length === 0) {
    return null;
  }

  const isPlaceholderData =
    (isStatsFeatureEnabled && statsMicroserviceQueryResult.isPlaceholderData) ||
    statsApiQueryResult.isPlaceholderData;
  const hasData = Boolean(
    statsApiQueryResult?.data || statsMicroserviceQueryResult?.data,
  );

  const { value: indicatorValue, valueDiff: indicatorValueDiff } =
    getIndicatorValues(
      selectedIndicatorData as TChainIndicator,
      statsMicroserviceQueryResult?.data,
      statsApiQueryResult?.data,
    );

  const formatValue = (value: string): string => {
    if (selectedIndicator === 'daily_txs') {
      return (
        getIndicatorValues(
          indicators[0],
          statsMicroserviceQueryResult?.data,
          statsApiQueryResult?.data,
        ).value?.toString() || ''
      );
    }
    if (!value) return value;

    // Check if the value is a number (with or without $ or other currency symbols)
    const numericValue = value.replace(/[^0-9.-]+/g, '');
    if (!isNaN(Number(numericValue))) {
      // Extract any prefix (like $ or other currency symbols)
      const prefixMatch = value.match(/^[^0-9.-]+/);
      const prefix = prefixMatch ? prefixMatch[0] : '';

      // Format number with exactly 2 decimal places
      const num = Number(numericValue);
      const withTwoDecimals = parseFloat(num.toFixed(2));

      // Add back thousand separators while keeping exactly 2 decimal places
      const formattedNumber = withTwoDecimals.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return prefix + formattedNumber;
    }

    // Return original value if not a number
    return value;
  };

  const title = (() => {
    let title: string | undefined;
    if (
      isStatsFeatureEnabled &&
      selectedIndicatorData?.titleMicroservice &&
      statsMicroserviceQueryResult?.data
    ) {
      title = selectedIndicatorData.titleMicroservice(
        statsMicroserviceQueryResult.data,
      );
    }

    return title || selectedIndicatorData?.title;
  })();

  const hint = (() => {
    let hint: string | undefined;
    if (
      isStatsFeatureEnabled &&
      selectedIndicatorData?.hintMicroservice &&
      statsMicroserviceQueryResult?.data
    ) {
      hint = selectedIndicatorData.hintMicroservice(
        statsMicroserviceQueryResult.data,
      );
    }

    return hint || selectedIndicatorData?.hint;
  })();

  const valueTitle = (() => {
    if (isPlaceholderData) {
      return <Skeleton h="36px" w="155px"/>;
    }

    if (!hasData) {
      return <Text fontSize="xs">There is no data</Text>;
    }

    if (indicatorValue === 'N/A') {
      return (
        <Text fontWeight={ 700 } fontSize="30px" lineHeight="36px">
          N/A
        </Text>
      );
    }

    const formattedValue =
      typeof indicatorValue === 'string' ?
        formatValue(indicatorValue) :
        indicatorValue;

    return (
      <Text fontWeight={ 700 } fontSize="30px" lineHeight="36px">
        { formattedValue }
      </Text>
    );
  })();

  const valueDiff = (() => {
    if (indicatorValueDiff === undefined || indicatorValueDiff === null) {
      return null;
    }

    const diffColor = indicatorValueDiff >= 0 ? 'green.500' : 'red.500';

    return (
      <Skeleton
        isLoaded={ !statsApiQueryResult.isPlaceholderData }
        display="flex"
        alignItems="center"
        color={ diffColor }
        ml={ 2 }
      >
        <IconSvg
          name="arrows/up-head"
          boxSize={ 5 }
          mr={ 1 }
          transform={ indicatorValueDiff < 0 ? 'rotate(180deg)' : 'rotate(0)' }
        />
        <Text color={ diffColor } fontWeight={ 600 }>
          { indicatorValueDiff }%
        </Text>
      </Skeleton>
    );
  })();

  return (
    <Flex
      px={{ base: 3, lg: 4 }}
      py={ 3 }
      borderRadius="12px"
      bgColor={ bgColor }
      columnGap={{ base: 3, lg: 4 }}
      rowGap={ 0 }
      flexBasis="50%"
      flexGrow={ 1 }
      alignItems="stretch"
    >
      <Flex flexGrow={ 1 } flexDir="column">
        <Flex
          justifyContent="space-between"
          alignItems="center"
          mb={{ base: 2, lg: 3 }}
        >
          <Flex alignItems="center">
            <Text fontWeight={ 500 }>{ title }</Text>
            { hint && <Hint label={ hint } ml={ 1 }/> }
          </Flex>
          <Flex alignItems="center">
            { valueTitle }
            { valueDiff }
          </Flex>
        </Flex>
        <Flex h={{ base: '150px' }} alignItems="flex-start" flexGrow={ 1 }>
          <ChainIndicatorChartContainer { ...queryResult }/>
        </Flex>
      </Flex>
      { indicators.length > 1 && (
        <Flex
          flexShrink={ 0 }
          flexDir="column"
          as="ul"
          borderRadius="lg"
          rowGap="6px"
          m={{ base: 'auto 0', lg: 0 }}
        >
          { indicators.map((indicator) => (
            <ChainIndicatorItem
              key={ indicator.id }
              id={ indicator.id }
              title={ indicator.title }
              icon={ indicator.icon }
              isSelected={ selectedIndicator === indicator.id }
              onClick={ selectIndicator }
              { ...getIndicatorValues(
                indicator,
                statsMicroserviceQueryResult?.data,
                statsApiQueryResult?.data,
              ) }
              isLoading={ isPlaceholderData }
              hasData={ hasData }
            />
          )) }
        </Flex>
      ) }
    </Flex>
  );
};

export default ChainIndicators;
